import React, { useEffect, useState } from "react";
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { db } from "../Utils/firebase";
import { FIREBASE_CONVERSATIONS } from "../Utils/constant";
import GroupEditModal from "./GroupEditModal";
import AddMemberModal from "./Chat/CreateGroupModal";

// Right-hand "About" panel for group conversations: group name/description,
// member list with admin badges, add/remove member and group edit modals.
// Self-contained (subscribes to the conversation doc itself) so it can be
// dropped into any page that shows a group chat.
export default function GroupAboutSidebar({ conversationId, userProfile }) {
  const [group, setGroup] = useState(null);
  const [participantsData, setParticipantsData] = useState({});
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (!conversationId) return;

    const groupRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);
    const unsubscribe = onSnapshot(groupRef, (docSnap) => {
      setGroup(docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null);
    });

    return () => unsubscribe();
  }, [conversationId]);

  useEffect(() => {
    if (!group?.participantDetails) return;

    const fetchParticipants = async () => {
      const entries = Object.keys(group.participantDetails);

      const fetchedData = await Promise.all(
        entries.map(async (userId) => {
          const userRef = doc(db, "users", String(userId));
          const userSnap = await getDoc(userRef);
          return [
            userId,
            userSnap.exists()
              ? userSnap.data()
              : { profileImage: "/icons/group-user.svg", name: "Unknown" },
          ];
        }),
      );

      setParticipantsData(Object.fromEntries(fetchedData));
    };

    fetchParticipants();
  }, [group]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        setUsers(
          querySnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          })),
        );
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchAllUsers();
  }, []);

  const handleAddMember = async (selectedMembers) => {
    if (!group?.id) {
      console.error("No current group loaded");
      return;
    }

    try {
      const groupRef = doc(db, FIREBASE_CONVERSATIONS, group.id);

      const newParticipantIds = selectedMembers.map((m) => m.id);
      const participantDetailsUpdate = {};

      selectedMembers.forEach((member) => {
        participantDetailsUpdate[member.id] = {
          name: member.name,
          email: member.email,
          profileImage: member.profileImage || "",
        };
      });

      await updateDoc(groupRef, {
        participants: newParticipantIds, // ✅ overwrite with exact list
        participantDetails: participantDetailsUpdate, // ✅ overwrite details
        visibleTo: arrayUnion(...newParticipantIds), // ✅ ensure visibility
      });
    } catch (error) {
      console.error("Failed to add members:", error);
    }
  };

  const removeMemberFromGroup = async (memberId) => {
    if (!group || !userProfile) return;

    if (!window.confirm("Are you sure you want to remove this member?")) return;

    try {
      const batch = writeBatch(db);
      const conversationRef = doc(db, FIREBASE_CONVERSATIONS, group.id);

      const updates = {
        participants: arrayRemove(memberId),
        visibleTo: arrayRemove(memberId),
        unreadUserIds: arrayRemove(memberId),
        [`participantDetails.${memberId}`]: deleteField(),
        [`unreadCount.${memberId}`]: deleteField(),
        updatedAt: serverTimestamp(),
      };

      batch.update(conversationRef, updates);

      // Update messages to add removed members to hiddenFor
      const messagesSnapshot = await getDocs(
        collection(db, FIREBASE_CONVERSATIONS, group.id, "messages"),
      );

      messagesSnapshot.forEach((msgDoc) => {
        const data = msgDoc.data();
        const hiddenFor = data.hiddenFor || [];
        if (!hiddenFor.includes(memberId)) {
          batch.update(msgDoc.ref, {
            hiddenFor: arrayUnion(memberId),
          });
        }
      });

      await batch.commit();
      toast.success("Member removed successfully");
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error("Failed to remove member");
    }
  };

  const handleGroupUpdate = async (updatedGroupInfo) => {
    try {
      const groupRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);

      await updateDoc(groupRef, {
        "metadata.groupName": updatedGroupInfo.name,
        "metadata.groupDescription": updatedGroupInfo.description,
      });
    } catch (error) {
      console.error("Error updating metadata:", error);
    }
  };

  const handleEditClick = () => {
    if (!group) {
      toast.error("Please select a group first.");
      return;
    }
    setShowEditModal(true);
  };

  const handleAddMemberClick = () => {
    if (!group) {
      toast.error("Please select a group first.");
      return;
    }
    setShowAddMemberModal(true);
  };

  if (!group || group.type !== "group") return null;

  const description =
    group.metadata?.groupDescription || "No Description Available";
  // Show only first 100 chars if not expanded
  const shortDesc =
    description.length > 100 ? description.slice(0, 100) + "..." : description;

  const participantIds = group.participantDetails
    ? Object.keys(group.participantDetails)
    : [];
  const usersNotInGroup = users.filter(
    (user) => !participantIds.includes(user.id),
  );

  return (
    <div className="squads-right-bar">
      <div className="d-flex justify-content-between mt-1 mb-2">
        <span className="text-body-b1">About</span>
        <a
          className="text-body-b3 squads-edits"
          role="button"
          onClick={handleEditClick}
        >
          <span>+</span> Edit
        </a>
      </div>
      <div className="d-flex gap-2 flex-column">
        <span className="squads-message-title mb-1">
          {group.metadata?.groupName || "No Title Available"}
        </span>
        <span
          className="text-body-b4 message-title-content-squads"
          style={{ whiteSpace: "pre-line" }}
        >
          {expanded ? description : shortDesc}
        </span>
        {description.length > 100 && (
          <a
            href="#!"
            className="text-body-b3 squads-edits"
            onClick={(e) => {
              e.preventDefault();
              setExpanded(!expanded);
            }}
          >
            {expanded ? "+ See Less" : "+ See More"}
          </a>
        )}
      </div>
      <hr
        className="my-3 my-md-4"
        style={{
          borderTop: "1px solid var(--Bg-Border, #1b2c3a)",
          opacity: 1,
        }}
      />
      <div
        className="no-scrollbar flex-grow-1"
        style={{
          height: "0px",
          minHeight: "0px",
        }}
      >
        <div className="d-flex gap-2 flex-column mb-3">
          <div className="d-flex align-items-center">
            <span className="text-body-b1">Members</span>
          </div>
          <span className="text-body-b4">
            Total Members ({group.participants?.length || 0})
          </span>
          <span
            className="text-body-b3"
            style={{
              color: "#008cff",
            }}
            onClick={handleAddMemberClick}
            role="button"
          >
            <span>+</span> Add Member
          </span>
        </div>
        <AddMemberModal
          isOpen={showAddMemberModal}
          onClose={() => setShowAddMemberModal(false)}
          onAddMember={handleAddMember}
          membersAlreadyInGroup={group.participantDetails}
          membersNotInGroup={usersNotInGroup}
          onRemoveMember={() => {}}
        />
        {Object.entries(participantsData).map(([userId, participant]) => (
          <div key={userId} className="chat-items">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3 justify-content-between align-items-center">
                <img
                  src={participant.profileImage}
                  className="profile-img"
                  alt={participant.name}
                />
                <div>
                  <div className="d-flex align-items-center">
                    <span className="text-body-b1">{participant.name}</span>
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span
                  className="text-small-s2"
                  style={{
                    color: "#c8ced3",
                  }}
                >
                  {group.metadata?.adminList?.includes(String(userId))
                    ? "Admin"
                    : ""}
                </span>
                {group.metadata?.adminList?.includes(String(userProfile?.id)) &&
                  String(userId) !== String(userProfile?.id) && (
                    <img
                      src="/icons/delete-icon.svg"
                      alt="Remove"
                      style={{
                        cursor: "pointer",
                        width: "16px",
                        height: "16px",
                      }}
                      onClick={() => removeMemberFromGroup(userId)}
                      title="Remove Member"
                    />
                  )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {showEditModal && (
        <GroupEditModal
          onClose={() => setShowEditModal(false)}
          group={group}
          onSave={handleGroupUpdate}
        />
      )}
    </div>
  );
}
